import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ConsultingLinks, type ConsultingPath } from "@/components/ConsultingExpertise";
import { motion } from "motion/react";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { AuthorCard } from "@/components/AuthorCard";
import { SOLUTIONS } from "@/lib/site";
import { sectionImageMeta } from "@/lib/ogSections";
import { SOLUTION_OG_CARDS, absoluteOg } from "@/lib/ogCards";
import { pageSchemaScript, PERSON_ID, SITE_URL } from "@/seo/schema";

import {
  ArrowRight,
  ArrowUpRight,
  AlertTriangle,
  Compass,
  Layers,
  ShieldCheck,
  Building2,
  Home,
  Factory,
  Landmark,
  GraduationCap,
  Hotel,
  
  Cpu,
  Eye,
  Radar,
  Lock,
  Network,
  Activity,
  Gauge,
  Server,
  MonitorSpeaker,
  Speaker,
  Wand2,
  Video,
  Lightbulb,
  Mic2,
  Tv,
  Cable,
  Wifi,
  type LucideIcon,
} from "lucide-react";

type SolutionItem = (typeof SOLUTIONS)[number];

type ProjectCard = { title: string; tag: string; image: string };
type InsightCard = { title: string; category: string; slug: string };

type SolutionDetail = {
  heroHeading: string;
  heroSub: string;
  intro: string;
  challenges: { title: string; body: string }[];
  approach: string;
  components: { title: string; body: string; Icon: LucideIcon }[];
  applications: { title: string; Icon: LucideIcon }[];
  mistakes: string[];
  faqs: { q: string; a: string }[];
  relatedProjects: ProjectCard[];
  relatedInsights: InsightCard[];
};

// Shared process — identical visual treatment, Apple-timeline style
const PROCESS_STEPS = [
  { n: "01", title: "Understand Environment", body: "Deep discovery: site, threat model, operational reality, owner intent." },
  { n: "02", title: "Design Architecture", body: "Vendor-neutral systems architecture aligned to outcomes — not catalogues." },
  { n: "03", title: "Integrate Systems", body: "Engineer the program as one fabric, not a stack of independent products." },
  { n: "04", title: "Validate Performance", body: "Measure, calibrate, prove. Nothing is signed off until it performs." },
  { n: "05", title: "Optimize Experience", body: "Refine the human and operator experience over the long lifecycle." },
];

// Generic placeholder project/insight images reused from assets
import projPerimeter from "@/assets/sol-perimeter.jpg";
import projCinema from "@/assets/sol-cinema.jpg";
import projSecurity from "@/assets/sol-security.jpg";
import projAuto from "@/assets/sol-automation.jpg";
import projAud from "@/assets/sol-auditorium.jpg";
import projEnt from "@/assets/sol-enterprise.jpg";

const GLOSSARY: { term: string; body: string }[] = [
  { term: "VLAN (Virtual LAN)", body: "A separate logical network on shared cabling — cameras, access control and BMS each get their own VLAN so traffic stays isolated and easier to secure." },
  { term: "RBAC (Role-Based Access Control)", body: "Permissions are granted to roles (operator, supervisor, FM head) rather than individual users — clean, auditable, easier to revoke." },
  { term: "ANPR / LPR", body: "Automatic Number-Plate Recognition — reads vehicle plates at gates and barriers for whitelists, visitors and incident lookup." },
  { term: "ONVIF", body: "An open standard that lets cameras and recorders from different brands talk to each other — protects you from single-vendor lock-in." },
  { term: "BACnet / Modbus", body: "Open protocols used by BMS, HVAC and energy systems to exchange data — the building's nervous system." },
  { term: "PoE (Power over Ethernet)", body: "One cable carries data and power to a camera, access reader or AP — simpler installation, fewer points of failure." },
  { term: "IBMS vs BMS", body: "BMS runs HVAC and electrical. IBMS integrates BMS with security, fire, access, AV and energy under one operating model." },
  { term: "PAVA", body: "Public Address / Voice Alarm — code-compliant intelligible voice evacuation, required for crowded venues and large buildings." },
  { term: "H.265 / H.265+ (Smart Codec)", body: "Modern video compression — typically 40–60% less storage than H.264 at the same forensic quality." },
  { term: "MTBF", body: "Mean Time Between Failures — the right metric for picking industrial gates, barriers and infrastructure-grade equipment." },
  { term: "RAID", body: "Storage redundancy — protects recordings against single-disk failure. Plan capacity after RAID overhead, not before." },
  { term: "SLA", body: "Service Level Agreement — written response and resolution times that an AMC must meet. Vague SLAs aren't SLAs." },
];

const DETAILS: Record<string, SolutionDetail> = {

  "security-infrastructure": {
    heroHeading: "Security Infrastructure for Intelligent Environments",
    heroSub: "Technology systems designed for secure, scalable and future-ready environments.",
    intro: "Mission-critical assets demand layered, intelligent protection — architected from a threat model, not a product catalogue.",
    challenges: [
      { title: "Isolated systems that don't communicate", body: "CCTV, access and intrusion live in silos with no unified policy or operational view." },
      { title: "Lack of scalability", body: "Brittle deployments that can't absorb new sites, sensors or business growth." },
      { title: "Blind spots in surveillance", body: "Coverage gaps, poor analytics, and unverifiable footage when it matters most." },
      { title: "High operational complexity", body: "Operators juggling four consoles instead of one unified pane of glass." },
      { title: "Reactive security approach", body: "Forensic review after incidents instead of proactive detect-deter-delay-respond." },
      { title: "Integration challenges", body: "OEM lock-in and undocumented interfaces blocking the next upgrade cycle." },
    ],
    approach: "Start from the threat model and operational reality. Architect a vendor-neutral, layered stack — detect, deter, delay, respond — integrated with command-and-control and 24×7 monitoring.",
    components: [
      { title: "Surveillance Systems", body: "Edge-AI cameras, VMS, video search and forensic toolchain.", Icon: Video },
      { title: "Access Control", body: "Logical and physical access governed by a single policy engine.", Icon: Lock },
      { title: "Intrusion Detection", body: "Multi-sensor intrusion with high-confidence event classification.", Icon: ShieldCheck },
      { title: "Monitoring Platforms", body: "24×7 SOC tooling with SLA-bound response coordination.", Icon: Eye },
      { title: "Analytics", body: "AI-led pattern detection, anomaly scoring, behavioural intelligence.", Icon: Activity },
      { title: "Command Center Integration", body: "One operator console unifying every subsystem and field force.", Icon: Server },
    ],
    applications: [
      { title: "Luxury Residences", Icon: Home },
      { title: "Industrial Facilities", Icon: Factory },
      { title: "Enterprise Buildings", Icon: Building2 },
      { title: "Auditoriums", Icon: GraduationCap },
      { title: "Critical Infrastructure", Icon: Landmark },
    ],
    mistakes: [
      "Buying devices before planning architecture",
      "Choosing products instead of outcomes",
      "Ignoring scalability and lifecycle cost",
      "Poor integration planning between subsystems",
      "Underinvesting in operator UX and training",
    ],
    faqs: [
      { q: "What does a security infrastructure strategist actually do?", a: "Operates as an owner-side advisor — threat modelling, architecture, vendor selection, integration design and lifecycle governance, independent of any single OEM." },
      { q: "Do you replace existing infrastructure?", a: "Only where it materially improves outcomes. Most programs are evolutionary; existing cameras, controllers and cabling are reused wherever viable." },
      { q: "What scale do you typically work at?", a: "From single luxury estates in New Delhi, Noida and Gurugram to multi-site industrial campuses and North India government programs." },
      { q: "Which OEMs and platforms do you work with?", a: "Vendor-neutral — Axis, Hanwha, Bosch, Milestone, Genetec, HID, LenelS2, Honeywell, and Indian OEMs selected against the brief." },
      { q: "How is success measured?", a: "Reduced incident dwell time, fewer false alarms, audit-clean compliance and a defined 10-year total-cost-of-ownership envelope." },
      { q: "Do you serve clients outside New Delhi?", a: "Yes — programs delivered across India with execution teams in Mumbai, Bengaluru and major metros." },
      { q: "How long does a typical engagement take?", a: "From a 6-week strategy sprint to multi-year programs depending on scale and number of sites." },
      { q: "Can the architecture support future AI upgrades?", a: "Yes — designs assume modular AI analytics and open VMS APIs from day one." },
      { q: "Is the system audit and compliance ready?", a: "Yes — CEA, MHA and sector-specific regulators are referenced during architecture." },
    ],
    relatedProjects: [
      { title: "Critical Infrastructure Surveillance Grid", tag: "Government", image: projSecurity },
      { title: "Multi-Site Enterprise Security Estate", tag: "Enterprise", image: projEnt },
      { title: "Private Estate · Integrated Security", tag: "Luxury", image: projCinema },
    ],
    relatedInsights: [
      { title: "AI in Perimeter Security: The 2030 Stack", category: "Security Trends", slug: "ai-in-perimeter-security-2030" },
      { title: "CCTV vs Perimeter Protection: A Strategic Comparison", category: "Security", slug: "perimeter-vs-cctv" },
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
    ],
  },
  "cctv-command-centre": {
    heroHeading: "CCTV & Command Centre Solutions",
    heroSub: "Edge-AI surveillance, video management and a unified command console — designed for Delhi NCR, Mumbai and pan-India estates.",
    intro: "Most CCTV estates are a wall of disconnected NVRs and forgotten footage. A real command centre fuses cameras, analytics and operator workflow into one decision system — auditable, searchable and ready when incidents happen.",
    challenges: [
      { title: "Cameras without analytics", body: "Hundreds of streams, no AI classification, no operator alerting — recording for forensics only." },
      { title: "Fragmented VMS estates", body: "Multiple recorders, multiple UIs, no portfolio-wide search across sites." },
      { title: "Poor low-light & licence-plate capture", body: "Wrong optics, wrong placement, wrong bitrate — footage that fails when it's needed." },
      { title: "Storage and retention sprawl", body: "Unpredictable storage costs and unclear legal retention compliance." },
      { title: "No command centre discipline", body: "Operators reactive instead of running event-driven SOPs." },
      { title: "Cyber-vulnerable IP cameras", body: "Default passwords, exposed ports, unsigned firmware — a soft underbelly." },
    ],
    approach: "Architect the CCTV estate as a command system: edge-AI cameras (Axis, Hanwha, Bosch, Dahua, Hikvision) on an open VMS (Milestone, Genetec, NX Witness), unified search, hardened network and an operator console designed for verified response — installed across luxury residences, corporate campuses and industrial sites in Delhi NCR, Noida, Gurugram, Mumbai and Bengaluru.",
    components: [
      { title: "Edge-AI Cameras", body: "4K, low-light and thermal optics from Axis, Hanwha, Bosch, Dahua, Hikvision.", Icon: Video },
      { title: "Video Management System", body: "Open VMS — Milestone XProtect, Genetec Security Center, NX Witness.", Icon: Server },
      { title: "Video Analytics", body: "Intrusion, loitering, ANPR, people-counting and behavioural anomaly.", Icon: Cpu },
      { title: "Command & Control Console", body: "One pane of glass for live, search, response and audit.", Icon: Eye },
      { title: "Storage & Retention", body: "Tiered storage with legal-retention policy baked in.", Icon: Layers },
      { title: "Network & Cyber Hardening", body: "Segmented VLANs, signed firmware, role-based access, audit logging.", Icon: Network },
    ],
    applications: [
      { title: "Luxury Residences", Icon: Home },
      { title: "Corporate Campuses", Icon: Building2 },
      { title: "Hospitality", Icon: Hotel },
      { title: "Industrial Sites", Icon: Factory },
      { title: "Critical Infrastructure", Icon: Landmark },
    ],
    mistakes: [
      "Specifying cameras before designing the operator workflow",
      "Mixing closed VMS with proprietary cameras",
      "Skipping a proper site survey for optics and lux levels",
      "No cyber-hardening or password governance plan",
      "Treating storage as an afterthought instead of a policy",
    ],
    faqs: [
      { q: "Which CCTV brands do you specify?", a: "Vendor-neutral — Axis, Hanwha, Bosch, Dahua, Hikvision, Pelco and Indian OEMs selected against the brief, lighting and retention requirements." },
      { q: "Which VMS platforms do you work with?", a: "Open VMS only — Milestone XProtect, Genetec Security Center, NX Witness and Bosch BVMS." },
      { q: "Do you provide a manned command centre?", a: "Yes — optional 24×7 monitoring with SLA-bound response coordination, operating from client SOCs or partner facilities." },
      { q: "Can existing cameras be reused?", a: "Wherever they're ONVIF-compliant and meet image-quality targets. Typical retrofits reuse 40–70% of the existing estate." },
      { q: "Do you handle ANPR and licence plate capture?", a: "Yes — purpose-built ANPR cameras with VMS integration for gate, parking and perimeter use cases." },
      { q: "What kind of CCTV projects have you delivered across Delhi NCR?", a: "Luxury residences and farmhouses, corporate headquarters, hospitality flagships and industrial estates across Delhi NCR, Mumbai, Lucknow, Bengaluru and North India. Specific client and site references are shared selectively and confidentially during scoping." },
      { q: "How is storage and retention costed?", a: "Tiered storage — hot, warm, cold — sized to the legal-retention policy (typically 30–90 days at full resolution) with predictable annual cost." },
      { q: "How is the system cyber-hardened?", a: "Network segmentation, default-password elimination, signed firmware, RBAC, audit logging and quarterly vulnerability review." },
      { q: "Is the system compliant with Indian regulations?", a: "Yes — CEA, DoT, MHA and sector-specific (CISF, BCAS) guidelines referenced at architecture stage." },
    ],
    relatedProjects: [
      { title: "Corporate HQ · 600-Camera Command Centre", tag: "Enterprise", image: projSecurity },
      { title: "Luxury Estate · Edge-AI Surveillance", tag: "Luxury", image: projCinema },
      { title: "Industrial Park · ANPR + Perimeter VMS", tag: "Industrial", image: projEnt },
    ],
    relatedInsights: [
      { title: "AI in Perimeter Security: The 2030 Stack", category: "Security Trends", slug: "ai-in-perimeter-security-2030" },
      { title: "CCTV vs Perimeter Protection: A Strategic Comparison", category: "Security", slug: "perimeter-vs-cctv" },
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
    ],
  },
  "access-control-biometrics": {
    heroHeading: "Access Control & Biometrics",
    heroSub: "Facial, fingerprint, card and mobile credentials — governed by one policy engine across every door.",
    intro: "Access control fails when it's bolted on door by door. The right system is a policy engine that knows every person, every door, every time-zone — integrated with HR, visitor management and the CCTV evidence trail.",
    challenges: [
      { title: "Per-door controllers with no policy", body: "Each door manages its own list — no central revocation, no audit trail." },
      { title: "Shared cards and tail-gating", body: "Credentials passed around and doors held open — the most common control failure." },
      { title: "Visitor process on paper", body: "Reception logbooks instead of pre-registered, time-bound digital passes." },
      { title: "Weak integration with HR / Active Directory", body: "Joiners and leavers not de-provisioned for days or weeks." },
      { title: "Biometric spoofing risk", body: "Cheap readers fooled by photos and silicon prints." },
      { title: "Fire & life-safety conflicts", body: "Doors that fail-secure when code requires fail-safe in evacuation." },
    ],
    approach: "Design access as one policy engine spanning every door, lift, turnstile and parking gate. Pair anti-spoof biometrics (HID, IDEMIA, Suprema, Matrix, ZKTeco, Hanwha) with mobile credentials, deep HR integration and a visitor management workflow — code-compliant for fire egress and audit-ready for regulators.",
    components: [
      { title: "Biometric Readers", body: "Liveness-tested facial, fingerprint and palm-vein readers.", Icon: Lock },
      { title: "Card & Mobile Credentials", body: "HID Origo, OSDP cards, Apple Wallet and Bluetooth credentials.", Icon: Cpu },
      { title: "Policy Engine", body: "Centralised access — Lenel S2, Honeywell Pro-Watch, Genetec Synergis, Matrix COSEC.", Icon: Server },
      { title: "Visitor Management", body: "Pre-registration, e-passes, watchlist screening and concierge UX.", Icon: Activity },
      { title: "HR / AD Integration", body: "Auto-provisioning and de-provisioning from HRMS and Active Directory.", Icon: Network },
      { title: "Fire & Life-Safety Interlock", body: "Code-compliant fail-safe behaviour on alarm.", Icon: ShieldCheck },
    ],
    applications: [
      { title: "Corporate Offices", Icon: Building2 },
      { title: "Luxury Residences", Icon: Home },
      { title: "Hospitality", Icon: Hotel },
      { title: "Education Campuses", Icon: GraduationCap },
      { title: "Critical Infrastructure", Icon: Landmark },
    ],
    mistakes: [
      "Per-door controllers without a central policy engine",
      "Cheap biometrics with no liveness detection",
      "No integration with HR for joiner / leaver flows",
      "Ignoring fire-code egress requirements",
      "Visitor process left as a paper logbook",
    ],
    faqs: [
      { q: "Which access control brands do you specify?", a: "Vendor-neutral — HID, LenelS2, Honeywell, Genetec, IDEMIA, Suprema, Matrix and ZKTeco selected against the policy, integration and lifecycle needs." },
      { q: "Are biometric readers spoof-proof?", a: "Specified readers use 3D liveness detection (IR + structured light) and are certified to iBeta PAD Level 2 where the brief requires it." },
      { q: "Do you support mobile credentials?", a: "Yes — HID Origo, Suprema mobile, Apple Wallet and Bluetooth credentials are standard on new engagements." },
      { q: "Can the system integrate with our HRMS / Active Directory?", a: "Yes — SCIM, LDAP and REST integration with SAP SuccessFactors, Workday, Darwinbox and Active Directory is routine." },
      { q: "Is the system DPDP Act compliant?", a: "Yes — biometric templates are stored as one-way hashes, with consent capture and retention policy designed in." },
      { q: "Do you handle parking and vehicle access?", a: "Yes — covered jointly with the Entrance, Gate & Parking Automation practice (ANPR, boom barriers, RFID tags)." },
      { q: "What about lift / elevator access control?", a: "Yes — destination control system (DCS) integration with KONE, Otis, Schindler and Mitsubishi." },
      { q: "Where have you delivered access control programs in India?", a: "Corporate HQs, government buildings, hospitality flagships and luxury estates across Delhi NCR, Mumbai, Lucknow, Bengaluru and North India. Specific project references are shared selectively and under confidentiality." },
    ],
    relatedProjects: [
      { title: "Corporate HQ · 200-Door Access Estate", tag: "Enterprise", image: projEnt },
      { title: "Luxury Residence · Biometric & Mobile Credentials", tag: "Luxury", image: projCinema },
      { title: "Manufacturing Campus · Turnstile + ANPR", tag: "Industrial", image: projSecurity },
    ],
    relatedInsights: [
      { title: "AI in Perimeter Security: The 2030 Stack", category: "Security Trends", slug: "ai-in-perimeter-security-2030" },
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
      { title: "CCTV vs Perimeter Protection", category: "Security", slug: "perimeter-vs-cctv" },
    ],
  },
  "fire-alarm-life-safety": {
    heroHeading: "Fire Alarm & Life Safety Systems",
    heroSub: "Addressable detection, voice evacuation and code-compliant life safety — engineered for Indian regulation and global insurance standards.",
    intro: "Life safety is non-negotiable, yet most buildings install the cheapest panel that passes inspection. A real life-safety program is engineered to NBC, NFPA and local fire NOC — and tested as if lives depend on it, because they do.",
    challenges: [
      { title: "Conventional panels in addressable buildings", body: "Zone-level granularity in buildings that need device-level identification." },
      { title: "Voice evacuation missing or unintelligible", body: "Alarm bells where voice messaging is mandated; STI scores that fail when tested." },
      { title: "No integration with BMS, access or HVAC", body: "Smoke not exhausted, doors not released, lifts not recalled on alarm." },
      { title: "Fragile commissioning and testing", body: "Systems handed over without device-by-device cause-and-effect validation." },
      { title: "Code drift between design, install and inspection", body: "Drawings, panel and reality diverging — a regulatory and insurance risk." },
      { title: "Aging panels with no spares", body: "End-of-life systems running on borrowed time." },
    ],
    approach: "Design to NBC 2016, NFPA 72 / 101 and local Fire NOC requirements. Specify addressable detection with voice evacuation, integrate cause-and-effect across HVAC, access, lifts and BMS, and validate the program device by device — using OEMs accepted by the local Chief Fire Officer and global insurance underwriters.",
    components: [
      { title: "Addressable Fire Panels", body: "Honeywell, Notifier, Siemens, Edwards, Bosch, Ravel, Agni — code-listed.", Icon: ShieldCheck },
      { title: "Smoke, Heat & Multi-Sensor Detection", body: "Device-level identification across every space.", Icon: Activity },
      { title: "Voice Evacuation (PAVA)", body: "EN 54-16 / -24 PAVA with intelligibility validated by STI test.", Icon: Speaker },
      { title: "Suppression Integration", body: "FM-200, NOVEC, water mist and sprinkler interfacing.", Icon: Layers },
      { title: "Cause-and-Effect with BMS", body: "Smoke control, door release, lift recall, HVAC shutdown.", Icon: Network },
      { title: "Graphics & Command", body: "Mimic / graphics workstation for the fire command centre.", Icon: Server },
    ],
    applications: [
      { title: "Corporate Towers", Icon: Building2 },
      { title: "Hospitality", Icon: Hotel },
      { title: "Education Campuses", Icon: GraduationCap },
      { title: "Industrial Facilities", Icon: Factory },
      { title: "Critical Infrastructure", Icon: Landmark },
    ],
    mistakes: [
      "Conventional panels in addressable buildings",
      "Skipping STI validation for voice evacuation",
      "No cause-and-effect testing with BMS / HVAC / lifts",
      "Choosing OEMs not accepted by the local Fire NOC",
      "Treating annual maintenance as a tick-box instead of a regime",
    ],
    faqs: [
      { q: "Which standards do you design to?", a: "NBC 2016 (India), NFPA 72 and NFPA 101, local State Fire Service requirements and the building's insurance underwriter standards." },
      { q: "Which fire alarm brands do you specify?", a: "Honeywell, Notifier, Siemens Cerberus, Edwards / EST, Bosch, Ravel and Agni — selected against Fire NOC acceptance, scale and lifecycle." },
      { q: "Do you handle Fire NOC documentation?", a: "Yes — drawings, calculations and submission packs prepared for the Chief Fire Officer and local fire department." },
      { q: "Is voice evacuation always required?", a: "Required for high-rises, malls, hospitals, hotels and large assembly buildings under NBC. We specify EN 54-16 / -24 PAVA with STI validation." },
      { q: "Do you integrate fire with BMS, access and lifts?", a: "Yes — cause-and-effect across smoke control, door release, lift recall and HVAC shutdown is part of every program." },
      { q: "Do you handle gas suppression for data centres?", a: "Yes — FM-200, NOVEC 1230 and water-mist systems integrated with detection and BMS." },
      { q: "What ongoing maintenance is required?", a: "Quarterly device testing, annual panel validation and a documented retest after any building modification." },
      { q: "Where have you delivered life-safety programs?", a: "Corporate towers, hospitality flagships, education campuses and industrial estates across Delhi NCR, Mumbai, Lucknow, Bengaluru and North India. Specific project references are shared selectively and under confidentiality." },
    ],
    relatedProjects: [
      { title: "Corporate Tower · Addressable + PAVA", tag: "Enterprise", image: projEnt },
      { title: "Hospitality Flagship · Integrated Life Safety", tag: "Hospitality", image: projSecurity },
      { title: "Data Centre · FM-200 + Detection", tag: "Critical", image: projAuto },
    ],
    relatedInsights: [
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
      { title: "AI in Perimeter Security: The 2030 Stack", category: "Security Trends", slug: "ai-in-perimeter-security-2030" },
      { title: "What 'Smart' Means for Indian Cities by 2030", category: "Smart Cities", slug: "smart-cities-future" },
    ],
  },
  "entrance-gate-parking-automation": {
    heroHeading: "Entrance, Gate & Parking Automation",
    heroSub: "Boom barriers, sliding gates, ANPR and visitor management — engineered as one entry experience for residences, corporates and logistics estates.",
    intro: "The entrance is the most-used and worst-designed part of most properties. The right answer is one system: automated gate, ANPR-driven barrier, pre-registered visitors and a guard console that actually helps the guard.",
    challenges: [
      { title: "Manual gates and long queues", body: "Single-lane chokepoints at peak hours — bad experience, weak security." },
      { title: "Underspecified gate motors", body: "Domestic-grade actuators on commercial gates — fail in 12–18 months." },
      { title: "No ANPR or whitelist", body: "Every vehicle hand-logged; resident and staff vehicles re-verified every day." },
      { title: "Visitors on paper", body: "Logbooks, photocopied IDs and no audit trail." },
      { title: "Disconnected parking guidance", body: "Drivers circling for spots; no occupancy, reservation or EV-charging visibility." },
      { title: "No coordination with CCTV and access", body: "Gate, camera and access reader behaving as three different products." },
    ],
    approach: "Engineer the entrance as one orchestrated journey: industrial-grade gate automation (FAAC, BFT, Nice, Came, Roger) paired with ANPR barriers (Magnetic, HUB, Nortech), a digital visitor management system and parking guidance / EV-charging — all surfaced on the guard console alongside CCTV and access.",
    components: [
      { title: "Sliding & Swing Gate Automation", body: "Industrial-grade actuators — FAAC, BFT, Nice, Came, Roger.", Icon: Wand2 },
      { title: "Boom Barriers", body: "High-cycle barriers — Magnetic, HUB, Nortech — with battery backup.", Icon: Activity },
      { title: "ANPR Cameras", body: "Whitelist, blacklist and visitor recognition at the gate.", Icon: Eye },
      { title: "Visitor Management", body: "Pre-registered e-passes, OTP verification, host approval, audit trail.", Icon: Network },
      { title: "Parking Guidance & EV", body: "Occupancy, reservation, payment and EV-charging integration.", Icon: Cpu },
      { title: "Guard Console", body: "One UI: gate, barrier, ANPR, CCTV, visitor approval, intercom.", Icon: Server },
    ],
    applications: [
      { title: "Luxury Residences & Gated Communities", Icon: Home },
      { title: "Corporate Campuses", Icon: Building2 },
      { title: "Hospitality", Icon: Hotel },
      { title: "Warehouses & Logistics", Icon: Factory },
      { title: "Government & Defence", Icon: Landmark },
    ],
    mistakes: [
      "Domestic-grade gate motors on commercial gates",
      "ANPR camera placed at the wrong height or angle",
      "No battery / UPS backup on barriers and gates",
      "Visitor process left manual after spending on automation",
      "Parking guidance procured separately from gate automation",
    ],
    faqs: [
      { q: "Which gate automation brands do you specify?", a: "Industrial-grade only — FAAC, BFT, Nice, Came, Roger and CAME — sized to the gate weight, duty cycle and climate." },
      { q: "Which boom barrier brands do you use?", a: "Magnetic Autocontrol, HUB Parking, Nortech, Came and FAAC for high-cycle commercial sites; battery backup standard." },
      { q: "What ANPR accuracy do you design for?", a: "≥98% on Indian licence plates under normal lighting, validated on-site. Optical, lighting and angle calibrated to the lane." },
      { q: "Do you handle visitor management as a standalone product?", a: "Yes — VMS platforms (myGate-grade for residential, Envoy / Proxyclick-grade for corporate) integrated with access and CCTV." },
      { q: "Can you integrate with society / community apps?", a: "Yes — APIs to MyGate, NoBrokerHood, ADDA and bespoke community platforms." },
      { q: "Do you handle EV charging in parking?", a: "Yes — AC and DC chargers with billing, app and parking-guidance integration." },
      { q: "What kinds of sites have you delivered across Delhi NCR?", a: "Gated communities, luxury residences and farmhouses, corporate campuses and logistics parks across Delhi NCR, Mumbai, Lucknow, Bengaluru and North India. Specific project references are shared selectively and under confidentiality." },
      { q: "How is performance measured?", a: "Throughput per lane per hour, visitor processing time, gate cycle reliability and incident dwell time at the entrance." },
    ],
    relatedProjects: [
      { title: "Gated Community · ANPR + Boom + VMS", tag: "Residential", image: projAuto },
      { title: "Corporate Campus · Tri-Lane Entrance", tag: "Enterprise", image: projEnt },
      { title: "Logistics Park · Truck ANPR & Dock Scheduling", tag: "Logistics", image: projPerimeter },
    ],
    relatedInsights: [
      { title: "CCTV vs Perimeter Protection: A Strategic Comparison", category: "Security", slug: "perimeter-vs-cctv" },
      { title: "AI in Perimeter Security: The 2030 Stack", category: "Security Trends", slug: "ai-in-perimeter-security-2030" },
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
    ],
  },
  "perimeter-protection": {
    heroHeading: "Perimeter Protection for Critical Environments",
    heroSub: "Multi-layer detection engineered for kilometres of demanding terrain.",
    intro: "Perimeters fail when detection is binary, response is slow, and weather kills accuracy. Sensor fusion fixes all three.",
    challenges: [
      { title: "High false-alarm rates", body: "Operators desensitised by nuisance triggers — the worst possible failure mode." },
      { title: "Coverage gaps in long perimeters", body: "Single-sensor solutions can't span kilometres of varied terrain reliably." },
      { title: "Difficult terrain & weather", body: "Monsoon, fog and heat-haze defeat any single sensing technology." },
      { title: "Slow human verification loops", body: "Minutes between detection and verified response — too slow for real intrusion." },
      { title: "Disconnected fence, CCTV and guard ops", body: "Three separate workflows running in parallel instead of one integrated SOP." },
    ],
    approach: "Sensor fusion — radar, fibre, thermal, video analytics — orchestrated by a single command layer with SOP-driven response protocols.",
    components: [
      { title: "Fibre Intrusion Detection", body: "Continuous detection along the entire boundary, immune to weather.", Icon: Cable },
      { title: "Ground & Drone Radar", body: "Wide-area detection beyond the fence line with low false-alarm rates.", Icon: Radar },
      { title: "Thermal Imaging", body: "All-weather verification at long range, day and night.", Icon: Eye },
      { title: "AI Video Analytics", body: "Classification, behaviour analysis and event correlation.", Icon: Cpu },
      { title: "Command & Control", body: "Single workflow unifying every sensor and the field response.", Icon: Server },
      { title: "Response SOPs", body: "Documented protocols binding guard force, technology and management.", Icon: Activity },
    ],
    applications: [
      { title: "Industrial Facilities", Icon: Factory },
      { title: "Critical Infrastructure", Icon: Landmark },
      { title: "Warehouses & Logistics", Icon: Building2 },
      { title: "Luxury Estates", Icon: Home },
      { title: "Government Sites", Icon: ShieldCheck },
    ],
    mistakes: [
      "Single-sensor procurement instead of fusion design",
      "No SOP for verified response",
      "Ignoring terrain and weather at design stage",
      "Treating perimeter as a fence, not a system",
    ],
    faqs: [
      { q: "How long can a perimeter program span?", a: "Programs led by Arif Khan have cumulatively secured 500km+ of perimeter, including individual sites running into tens of kilometres." },
      { q: "Can existing fence be reused?", a: "Often yes — fibre intrusion detection and analytics retrofit onto existing fencing, reducing capex meaningfully." },
      { q: "Which sensor technologies are used?", a: "Fibre PIDS, ground radar, thermal cameras, microwave, taut-wire and AI video analytics — combined per terrain, threat and budget." },
      { q: "How are false alarms controlled?", a: "Through sensor fusion, AI classification, scheduled tuning windows and an event-correlation engine in the command layer." },
      { q: "Is the system Indian-regulation compliant?", a: "Yes — designs respect MHA, MoD, CISF and sector-specific perimeter guidelines, and use approved OEMs where mandated." },
      { q: "Do you provide ongoing monitoring?", a: "Yes — optional 24×7 monitoring with SLA-bound response coordination with on-site guard forces." },
      { q: "What is the typical false-alarm rate target?", a: "Sub-5% across the integrated stack after tuning, validated against weeks of live operation." },
      { q: "How do you handle expansions and new sites?", a: "Reference designs allow rapid replication across new sites without redesigning from zero." },
    ],
    relatedProjects: [
      { title: "Multi-Kilometre Industrial Perimeter", tag: "Industrial", image: projPerimeter },
      { title: "Logistics Park · Fibre + Radar Fusion", tag: "Logistics", image: projSecurity },
      { title: "Government Site · Thermal + AI Analytics", tag: "Government", image: projEnt },
    ],
    relatedInsights: [
      { title: "AI in Perimeter Security: The 2030 Stack", category: "Security Trends", slug: "ai-in-perimeter-security-2030" },
      { title: "CCTV vs Perimeter Protection", category: "Security", slug: "perimeter-vs-cctv" },
      { title: "What 'Smart' Means for Indian Cities by 2030", category: "Smart Cities", slug: "smart-cities-future" },
    ],
  },
  "smart-automation": {
    heroHeading: "Smart Automation for Intelligent Environments",
    heroSub: "Unified control fabric for buildings that respond to people.",
    intro: "Most 'smart' homes are apps stitched together. Real automation is one fabric — deterministic, serviceable, designed for the next decade.",
    challenges: [
      { title: "Brand silos across subsystems", body: "Lighting, HVAC, AV and security each running their own brittle app." },
      { title: "Scenes that break on firmware updates", body: "No deterministic engineering, no regression testing, no documentation." },
      { title: "No real personalisation or learning", body: "Static automations instead of behavioural intelligence." },
      { title: "Weak network and power foundations", body: "Premium kit on consumer-grade Wi-Fi and unprotected power." },
      { title: "Owners and staff under-trained", body: "Operators left to discover the system instead of being onboarded." },
    ],
    approach: "Design a unified control fabric with deterministic scenes, robust network and power, and behavioural intelligence layered on top — auditable, serviceable, future-proof.",
    components: [
      { title: "Unified Controller", body: "Single brain — Crestron, Control4, Lutron, KNX or Savant.", Icon: Cpu },
      { title: "Network & Power", body: "Enterprise-grade network, segmented VLANs and clean power.", Icon: Wifi },
      { title: "Lighting & Shading", body: "Architectural lighting and motorised shading as one experience.", Icon: Lightbulb },
      { title: "Climate Integration", body: "HVAC, underfloor heating and ventilation coordinated as scenes.", Icon: Activity },
      { title: "AV Integration", body: "Distributed audio, video and cinema unified under one UI.", Icon: MonitorSpeaker },
      { title: "Security & Access", body: "Surveillance, intrusion and access governed inside the same fabric.", Icon: ShieldCheck },
    ],
    applications: [
      { title: "Luxury Residences", Icon: Home },
      { title: "Hospitality", Icon: Hotel },
      { title: "Corporate Offices", Icon: Building2 },
      { title: "Auditoriums", Icon: GraduationCap },
      { title: "Mixed-Use Buildings", Icon: Landmark },
    ],
    mistakes: [
      "Picking a platform before designing the experience",
      "Skimping on network and power foundations",
      "No scene engineering or documentation",
      "Ignoring owner and staff training",
    ],
    faqs: [
      { q: "Which platforms do you work with?", a: "Vendor-neutral — Crestron, Control4, Lutron, KNX, Savant and bespoke combinations based on the brief and lifecycle." },
      { q: "Do you provide ongoing service?", a: "Yes — documented SLAs, remote monitoring and scheduled health checks." },
      { q: "How long does a luxury automation project take?", a: "Typically 4–9 months from design to handover, parallel to civil and interior works." },
      { q: "Can existing homes be retrofitted?", a: "Yes — wireless-friendly KNX RF, Lutron RA3 and Control4 enable clean retrofits." },
      { q: "Is voice control supported?", a: "Yes — Alexa, Google and bespoke voice, with privacy boundaries designed in." },
      { q: "How is cyber-security handled?", a: "Segmented VLANs, firewalled IoT, signed firmware and audited remote access." },
      { q: "Can the system learn owner preferences?", a: "Yes — behavioural learning and adaptive scenes are part of the design where wanted." },
      { q: "What ongoing cost should I plan for?", a: "Typically 6–10% of capex annually for SLA, software and lifecycle health." },
    ],
    relatedProjects: [
      { title: "Private Residence · Unified Control Fabric", tag: "Luxury", image: projAuto },
      { title: "Hospitality Flagship · Guest Experience System", tag: "Hospitality", image: projEnt },
      { title: "Corporate HQ · Smart Building Backbone", tag: "Enterprise", image: projSecurity },
    ],
    relatedInsights: [
      { title: "Seven Mistakes That Wreck a Smart Home", category: "Home Automation", slug: "home-automation-mistakes" },
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
      { title: "What 'Smart' Means for Indian Cities by 2030", category: "Smart Cities", slug: "smart-cities-future" },
    ],
  },
  "luxury-home-cinema": {
    heroHeading: "Luxury Home Cinema, Engineered as an Environment",
    heroSub: "Acoustic shell, calibrated optics, reference audio — designed as one architecture.",
    intro: "A luxury cinema is an acoustic and architectural problem first, an equipment problem second. Most private theatres in India underperform their kit by 50% or more.",
    challenges: [
      { title: "Room acoustics ignored", body: "Premium kit placed in untreated rooms loses most of its capability instantly." },
      { title: "Equipment over-specified, under-tuned", body: "Reference gear shipped without calibration delivers mid-tier performance." },
      { title: "Poor seat ergonomics & sightlines", body: "Beautiful rooms with the wrong screen height and seating geometry." },
      { title: "HVAC noise floor too high", body: "Ambient noise stealing dynamic range from every scene." },
      { title: "Lighting and AV control disjointed", body: "Cinema mode as an afterthought, not an engineered scene." },
    ],
    approach: "Acoustic shell design, calibrated 4K HDR / laser projection, reference audio, integrated automation and architectural lighting — engineered as a complete environment.",
    components: [
      { title: "Acoustic Shell", body: "Isolation, absorption, diffusion engineered to the room.", Icon: Layers },
      { title: "Calibrated Display", body: "4K HDR laser projection or premium LED reference systems.", Icon: Tv },
      { title: "Reference Audio", body: "Dolby Atmos 7.2.4 → 9.4.6 with calibrated DSP.", Icon: Speaker },
      { title: "Low-Noise HVAC", body: "Engineered airflow with NC-20 ambient targets.", Icon: Activity },
      { title: "Cinema Automation", body: "One-touch scenes binding optics, audio, lighting and climate.", Icon: Wand2 },
      { title: "Architectural Lighting", body: "Layered lighting design that disappears when the lights dim.", Icon: Lightbulb },
    ],
    applications: [
      { title: "Luxury Residences", Icon: Home },
      { title: "Private Estates", Icon: Landmark },
      { title: "Boutique Hospitality", Icon: Hotel },
      { title: "Founder Offices", Icon: Building2 },
    ],
    mistakes: [
      "Specifying equipment before the room",
      "Ignoring acoustic isolation from the rest of the house",
      "Skipping calibration after install",
      "No structured owner handover",
    ],
    faqs: [
      { q: "What is the minimum room size?", a: "From 18 m² for an intimate cinema; ideally 25–40 m² for full reference performance with Dolby Atmos." },
      { q: "Is Dolby Atmos standard?", a: "Yes. Configurations from 7.2.4 up to 9.4.6 are routine, with reference-grade calibration." },
      { q: "Which projection technology do you specify?", a: "4K HDR triple-laser projectors from Sony, Barco, JVC and Christie, paired with acoustically transparent screens from Stewart, Screen Research and DNP." },
      { q: "What does a luxury home cinema cost in India?", a: "Reference-grade rooms typically range from ₹60 lakh to ₹4 crore depending on scale, finishes and seating." },
      { q: "Do you handle interiors and acoustics together?", a: "Yes — acoustic shell, finishes, seating and lighting are designed as one package with the interior architect." },
      { q: "Can existing rooms be converted?", a: "Yes, with acoustic isolation and a properly engineered shell built within the existing envelope." },
      { q: "Do you support LED video wall cinemas?", a: "Yes — direct-view LED reference systems are designed where ambient light or scale demands them." },
      { q: "What's the timeline?", a: "Typically 6–12 months from concept to calibrated handover, parallel to civil works." },
    ],
    relatedProjects: [
      { title: "Private Reference Cinema · New Delhi", tag: "Luxury", image: projCinema },
      { title: "Boutique Hotel · Atmos Screening Room", tag: "Hospitality", image: projAud },
      { title: "Founder Residence · Cinema + Cellar", tag: "Luxury", image: projAuto },
    ],
    relatedInsights: [
      { title: "The Luxury Home Cinema Planning Checklist", category: "Luxury Living", slug: "luxury-home-cinema-checklist" },
      { title: "Seven Mistakes That Wreck a Smart Home", category: "Home Automation", slug: "home-automation-mistakes" },
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
    ],
  },
  "auditorium-av": {
    heroHeading: "Auditorium AV for Institutional Stages",
    heroSub: "Acoustics, optics, lighting and control engineered for the way the venue is actually used.",
    intro: "Auditoriums fail their audience when acoustics, sightlines, lighting and AV are designed in silos. The fix is one integrated program.",
    challenges: [
      { title: "Speech intelligibility issues", body: "Audiences strain to hear from row 12 onwards — the most common failure." },
      { title: "Poor sightlines from peripheral seats", body: "Geometry ignored at design stage, impossible to fix afterwards." },
      { title: "Operator complexity", body: "Five remotes for one event — guaranteed mistakes during live use." },
      { title: "Hybrid and broadcast missing", body: "No way to extend the event to remote audiences cleanly." },
      { title: "Maintenance burden too high", body: "Specialist intervention required for every minor change." },
    ],
    approach: "Integrated acoustic, visual, lighting and control design — engineered for the venue's actual programming and operable by a small in-house team.",
    components: [
      { title: "Acoustic Modelling", body: "Predictive modelling tuned against the architectural geometry.", Icon: Layers },
      { title: "Display & Projection", body: "Laser projection or LED, sized to seat count and ambient light.", Icon: Tv },
      { title: "Line-Array Audio + DSP", body: "Even coverage to every seat with sub-1.0 second decay targets.", Icon: Speaker },
      { title: "Lighting & Rigging", body: "Theatrical lighting and rigging integrated with AV control.", Icon: Lightbulb },
      { title: "Broadcast & Streaming", body: "Multi-cam production, encoding and broadcast feeds.", Icon: Video },
      { title: "Operator UX", body: "Touch-driven UI an operator can run after one training session.", Icon: Wand2 },
    ],
    applications: [
      { title: "Education & Universities", Icon: GraduationCap },
      { title: "Corporate Auditoriums", Icon: Building2 },
      { title: "Cultural Institutions", Icon: Landmark },
      { title: "Houses of Worship", Icon: Mic2 },
      { title: "Hospitality Convention Halls", Icon: Hotel },
    ],
    mistakes: [
      "AV specified after the building is designed",
      "Acoustics treated as an afterthought",
      "No operator UX or training plan",
      "Streaming and broadcast added too late",
    ],
    faqs: [
      { q: "What auditorium capacities do you design for?", a: "200 to 2000+ seats, including hybrid, broadcast-ready and convertible venues." },
      { q: "Do you handle streaming and broadcast?", a: "Yes — designed in from day one with IP video, multi-cam production, encoding and broadcast feeds." },
      { q: "Which audio brands are typically specified?", a: "L-Acoustics, d&b audiotechnik, Meyer Sound, Renkus-Heinz and Shure — selected against acoustic modelling and budget." },
      { q: "Are LED walls or projection preferred?", a: "Both — LED for sustained brightness and broadcast, projection where scale and finesse matter more." },
      { q: "Do you assist with civil and acoustic works?", a: "Yes — coordinated with the architect, acoustic consultant and civil contractor as one program." },
      { q: "Is the system maintainable by an in-house team?", a: "Yes — UX, documentation and a structured training plan are part of the deliverable." },
      { q: "What speech-intelligibility target do you design for?", a: "STI > 0.6 across every seat, validated by on-site measurement." },
      { q: "Do you do retrofit of existing auditoriums?", a: "Yes — many engagements upgrade legacy venues without major civil works." },
    ],
    relatedProjects: [
      { title: "University Flagship Auditorium", tag: "Education", image: projAud },
      { title: "Corporate Town Hall · Broadcast-Ready", tag: "Enterprise", image: projEnt },
      { title: "Cultural Institution · Acoustic Reset", tag: "Cultural", image: projAuto },
    ],
    relatedInsights: [
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
      { title: "What 'Smart' Means for Indian Cities by 2030", category: "Smart Cities", slug: "smart-cities-future" },
      { title: "The Luxury Home Cinema Planning Checklist", category: "Luxury Living", slug: "luxury-home-cinema-checklist" },
    ],
  },
  "enterprise-av": {
    heroHeading: "Enterprise AV for Distributed Workplaces",
    heroSub: "A single AV experience across every room, every floor, every region.",
    intro: "Enterprise AV gets specified by IT, designed by integrators, and used by no one consistently. The fix is portfolio-wide standardisation.",
    challenges: [
      { title: "Inconsistent UX across rooms", body: "Every meeting starts with five minutes of figuring out the room." },
      { title: "Hybrid meeting fatigue", body: "Remote participants visibly second-class in every call." },
      { title: "High service overhead", body: "Tickets piling up because nothing is standardised or monitored." },
      { title: "Poor analytics on room utilisation", body: "Decisions about space made on intuition, not data." },
      { title: "Fragmented vendor landscape", body: "Different vendor per region, no consolidated reporting." },
    ],
    approach: "Standardise the AV stack across the portfolio with a consistent, simple user experience, central monitoring and lifecycle governance.",
    components: [
      { title: "Room Taxonomy", body: "Reference room types from huddle to executive boardroom.", Icon: Layers },
      { title: "Reference Designs", body: "Standardised stacks executed identically across regions.", Icon: Compass },
      { title: "Unified UCC Platform", body: "Teams Rooms, Zoom Rooms or Google Meet — one strategy.", Icon: Video },
      { title: "Central Monitoring", body: "Crestron XiO, Logitech Sync, Q-SYS Reflect dashboards.", Icon: Gauge },
      { title: "Change Management", body: "Pilot, validate, roll out — a disciplined deployment model.", Icon: Activity },
      { title: "Global Governance", body: "One vendor framework, vetted local partners, predictable rollout.", Icon: Network },
    ],
    applications: [
      { title: "Corporate Headquarters", Icon: Building2 },
      { title: "Global Offices", Icon: Landmark },
      { title: "Training Centres", Icon: GraduationCap },
      { title: "Control Rooms", Icon: Server },
      { title: "Hybrid Workspaces", Icon: MonitorSpeaker },
    ],
    mistakes: [
      "Designing room-by-room instead of portfolio-wide",
      "No central monitoring or analytics",
      "Ignoring hybrid meeting parity",
      "Choosing vendor before defining outcomes",
    ],
    faqs: [
      { q: "Microsoft Teams or Zoom Rooms?", a: "Both, plus native room systems where the use case demands — Teams Rooms, Zoom Rooms, Google Meet hardware and bespoke." },
      { q: "Do you support global rollouts?", a: "Yes — standardised reference designs executed across regions with vetted local partners." },
      { q: "Which OEMs are typically used?", a: "Logitech, Poly, Cisco, Yealink, Crestron, Q-SYS and Shure, selected against the room taxonomy." },
      { q: "How is the estate monitored?", a: "Central monitoring (Crestron XiO, Logitech Sync, Q-SYS Reflect) with SLA-tracked ticketing." },
      { q: "Can you take over an existing estate?", a: "Yes — typical engagements start with an audit, a standardisation roadmap and a 12–18 month migration plan." },
      { q: "How is success measured?", a: "Meeting-start success rate, ticket volume per room per year, and utilisation against capacity." },
      { q: "What is the typical engagement length?", a: "From a 90-day standardisation sprint to a multi-year global rollout." },
      { q: "How do you handle change management?", a: "Pilot rooms, structured user testing, communications plan and floor-walker support at go-live." },
    ],
    relatedProjects: [
      { title: "Global HQ · Standardised Meeting Estate", tag: "Enterprise", image: projEnt },
      { title: "Regional Office Rollout · 40 Rooms", tag: "Enterprise", image: projAuto },
      { title: "Control Room · Mission Operations", tag: "Critical", image: projSecurity },
    ],
    relatedInsights: [
      { title: "A CFO-Grade ROI Framework for Smart Buildings", category: "Technology Leadership", slug: "smart-building-roi-framework" },
      { title: "What 'Smart' Means for Indian Cities by 2030", category: "Smart Cities", slug: "smart-cities-future" },
      { title: "AI in Perimeter Security: The 2030 Stack", category: "Security Trends", slug: "ai-in-perimeter-security-2030" },
    ],
  },
};

// SEO overrides for titles that exceed 60 chars or descriptions <90 chars.
// Page content (heroHeading / heroSub) is unchanged.
const SEO_OVERRIDES: Record<string, { title?: string; description?: string }> = {
  "security-infrastructure": {
    title: "Security Infrastructure Consulting | Arif Khan",
    description:
      "Enterprise-grade security infrastructure — surveillance, access, intrusion, command-centre and analytics engineered as one fabric for mission-critical assets.",
  },
  "perimeter-protection": {
    description:
      "Multi-layer perimeter detection — fibre, radar, thermal and AI analytics correlated under one engine for kilometre-scale sites with low false-alarm rates.",
  },
  "smart-automation": {
    description:
      "Vendor-neutral smart automation — Lutron, KNX and Crestron unified for lighting, climate, shades, audio, cinema and security in intelligent buildings.",
  },
  "luxury-home-cinema": {
    title: "Luxury Home Cinema Design & Engineering | Arif Khan",
    description:
      "Reference-grade private cinemas — acoustic shell, isolation, 7.2.4 / 9.4.6 Atmos layouts and calibrated projection engineered before construction begins.",
  },

  "enterprise-av": {
    description:
      "Enterprise AV at scale — standardised boardrooms, training rooms, control centres and hybrid meeting estates with one operator experience portfolio-wide.",
  },
};

export const Route = createFileRoute("/solutions/$slug")({
  component: SolutionPage,
  loader: ({ params }): { solution: SolutionItem; detail: SolutionDetail } => {
    const solution = SOLUTIONS.find((s) => s.slug === params.slug);
    if (!solution) throw notFound();
    const detail = DETAILS[params.slug];
    if (!detail) throw notFound();
    return { solution, detail };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { solution, detail } = loaderData;
    const override = SEO_OVERRIDES[params.slug] ?? {};
    const seoTitle = override.title ?? `${detail.heroHeading} | Arif Khan`;
    const seoDescription = override.description ?? detail.heroSub;
    return {
      meta: [
        { title: seoTitle },
        { name: "description", content: seoDescription },
        { property: "og:title", content: seoTitle },
        { property: "og:description", content: seoDescription },
        { property: "og:url", content: `https://arifkhanglobal.com/solutions/${params.slug}` },
        { property: "og:type", content: "article" },
        ...sectionImageMeta(
          absoluteOg(SOLUTION_OG_CARDS[params.slug], solution.image),
          `${solution.title} — Arif Khan`,
        ),

      ],
      links: [
        { rel: "canonical", href: `https://arifkhanglobal.com/solutions/${params.slug}` },
        { rel: "alternate", type: "text/markdown", href: `https://arifkhanglobal.com/solutions/${params.slug}.md`, title: "Markdown mirror" },
      ],
      scripts: [
        pageSchemaScript(
          {
            path: `/solutions/${params.slug}`,
            type: "WebPage",
            name: seoTitle,
            description: seoDescription,
            breadcrumb: [
              { name: "Solutions", url: "/solutions" },
              { name: solution.title, url: `/solutions/${params.slug}` },
            ],
            extra: { about: { "@id": PERSON_ID } },
          },
          [
            {
              "@type": "Service",
              "@id": `${SITE_URL}/solutions/${params.slug}#service`,
              name: solution.title,
              description: detail.heroSub,
              serviceType: solution.title,
              url: `${SITE_URL}/solutions/${params.slug}`,
              provider: { "@id": PERSON_ID },
              areaServed: ["New Delhi", "Noida", "Gurugram", "Faridabad", "Ghaziabad", "North India", "Mumbai", "Bengaluru"],
            },
            ...(detail.faqs.length
              ? [
                  {
                    "@type": "FAQPage",
                    "@id": `${SITE_URL}/solutions/${params.slug}#faq`,
                    inLanguage: "en-IN",
                    isPartOf: { "@id": `${SITE_URL}/solutions/${params.slug}#webpage` },
                    mainEntity: detail.faqs.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                  },
                ]
              : []),
          ],
        ),
      ],
    };
  },
});

const SOLUTION_CONSULTING: Record<string, ConsultingPath[]> = {
  "security-infrastructure": ["/security-consultant-in-india", "/elv-consultant-in-india"],
  "perimeter-protection": ["/perimeter-security-expert-in-india", "/security-consultant-in-india"],
  "cctv-command-centre": ["/security-consultant-in-india", "/perimeter-security-expert-in-india"],
  "access-control-biometrics": ["/security-consultant-in-india"],
  "entrance-gate-parking-automation": ["/security-consultant-in-india"],
  "smart-automation": ["/elv-consultant-in-india"],
  "auditorium-av": ["/av-consultant-in-india"],
  "enterprise-av": ["/av-consultant-in-india", "/elv-consultant-in-india"],
  "luxury-home-cinema": ["/av-consultant-in-india"],
};

function SolutionPage() {
  const { solution, detail } = Route.useLoaderData() as {
    solution: SolutionItem;
    detail: SolutionDetail;
  };

  return (
    <PageShell>
      {/* SECTION 1 — Cinematic Hero */}
      <SectionHero solution={solution} detail={detail} />

      {/* SECTION 2 — Challenges */}
      <Section bg="surface" eyebrow="The Challenge" title="Common Challenges">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {detail.challenges.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05}>
              <div className="glass rounded-2xl border border-white/40 p-7 h-full hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-500">
                <AlertTriangle className="h-5 w-5 text-primary mb-5" strokeWidth={1.6} />
                <h3 className="text-base font-semibold tracking-tight">{c.title}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 3 — The Thinking (Process timeline) */}
      <Section eyebrow="The Thinking" title="How Arif Khan Approaches It" intro={detail.approach}>
        <div className="relative mt-4">
          <div aria-hidden className="absolute left-4 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
          <div className="space-y-6">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05}>
                <div className={`relative grid md:grid-cols-2 gap-6 md:gap-12 items-center ${i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"}`}>
                  <div className="pl-12 md:pl-0 md:text-right md:pr-12">
                    <div className="font-mono text-xs text-primary mb-2">{s.n}</div>
                    <h3 className="text-xl font-semibold tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-sm text-ink-soft leading-relaxed">{s.body}</p>
                  </div>
                  <div className="hidden md:block" />
                  <div aria-hidden className="absolute left-4 md:left-1/2 top-2 md:top-1/2 md:-translate-y-1/2 md:-translate-x-1/2 h-3 w-3 rounded-full bg-primary shadow-[0_0_0_6px_color-mix(in_oklab,var(--primary)_18%,transparent)]" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* SECTION 4 — Solution Framework */}
      <Section bg="surface" eyebrow="The Framework" title="Technology Components">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {detail.components.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.04}>
              <div className="group glass rounded-2xl border border-white/40 p-7 h-full hover:border-primary/40 hover:shadow-[0_30px_60px_-30px_rgba(21,101,192,0.35)] transition-all duration-500">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-500">
                  <c.Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
                </div>
                <h3 className="mt-6 text-base font-semibold tracking-tight">{c.title}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 5 — Applications */}
      <Section eyebrow="In the Field" title="Where It Gets Applied">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {detail.applications.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.04}>
              <div className="rounded-2xl border border-border bg-background p-6 h-full text-center hover:border-primary/40 transition-colors">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary-soft text-primary mb-4">
                  <a.Icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <div className="text-sm font-semibold leading-snug">{a.title}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 6 — Mistakes */}
      <Section bg="surface" eyebrow="Avoid These" title="Common Mistakes">
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
          {detail.mistakes.map((m, i) => (
            <Reveal key={m} delay={i * 0.04}>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-background p-6">
                <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                  <AlertTriangle className="h-4 w-4" strokeWidth={1.8} />
                </div>
                <p className="text-foreground leading-snug">{m}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 6.5 — Plain-English Glossary */}
      <Section bg="surface" eyebrow="Plain English" title="The terms you'll hear, explained" intro="Specifications and proposals across security, automation and AV reuse the same vocabulary. Here are the ones that matter most — without the jargon.">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl">
          {GLOSSARY.map((g, i) => (
            <Reveal key={g.term} delay={i * 0.02}>
              <div className="rounded-2xl border border-border bg-background p-6 h-full">
                <div className="text-sm font-semibold text-foreground">{g.term}</div>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{g.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 7 — FAQs */}
      <Section eyebrow="Questions" title="Frequently Asked Questions">

        <div className="max-w-3xl space-y-4">
          {detail.faqs.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.03}>
              <details className="group glass rounded-2xl border border-white/40 p-6">
                <summary className="flex items-center justify-between cursor-pointer list-none gap-4">
                  <span className="font-semibold text-foreground">{f.q}</span>
                  <span className="text-primary transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-ink-soft leading-relaxed">{f.a}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 8 — Related Projects */}
      <Section bg="surface" eyebrow="Reference" title="Related Projects">
        <div className="grid md:grid-cols-3 gap-5">
          {detail.relatedProjects.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <Link to="/projects" className="group block rounded-2xl overflow-hidden border border-border bg-background hover:border-border-strong transition-colors">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={p.image} alt={`${p.title} — ${p.tag.toLowerCase()} project reference`} width={1200} height={1500} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 scrim-bottom" />
                  <div className="absolute top-4 left-4 glass-dark rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-white/95">{p.tag}</div>
                </div>
                <div className="p-6 flex items-center justify-between gap-4">
                  <h3 className="text-base font-semibold leading-tight">{p.title}</h3>
                  <ArrowUpRight className="h-4 w-4 text-ink-soft group-hover:text-primary transition-colors" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 9 — Related Insights */}
      <Section eyebrow="Read More" title="Related Insights">
        <div className="grid md:grid-cols-3 gap-5">
          {detail.relatedInsights.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.06}>
              <Link to="/insights" className="group block h-full rounded-2xl border border-border bg-background p-7 hover:border-border-strong transition-colors">
                <div className="eyebrow text-accent">{a.category}</div>
                <h3 className="mt-5 text-lg font-semibold leading-snug tracking-tight">{a.title}</h3>
                <div className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
                  Read article <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/learning-center" className="inline-flex items-center gap-2 text-sm font-medium text-primary">
            Visit the Learning Center <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      {SOLUTION_CONSULTING[solution.slug] && (
        <ConsultingLinks
          intro="For project-level planning across India, see Arif Khan's related consulting expertise:"
          only={SOLUTION_CONSULTING[solution.slug]}
        />
      )}

      {/* Author */}
      <section className="py-16 border-t border-border bg-surface">
        <div className="container-page max-w-4xl">
          <AuthorCard />
        </div>
      </section>

      {/* SECTION 10 — Final CTA */}
      <FinalCTA />
    </PageShell>
  );
}

/* ───────────── Hero ───────────── */
function SectionHero({ solution, detail }: { solution: SolutionItem; detail: SolutionDetail }) {
  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img
          src={solution.image}
          alt={`${solution.title} — cinematic reference environment`}
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/65 to-background/30" />
        <div className="absolute inset-0 bg-radial-primary opacity-50" />
      </div>
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="container-page relative pb-24 pt-40">
        <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow text-primary">
          Solution Area · {solution.title}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 text-balance text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.0] max-w-5xl"
        >
          {detail.heroHeading}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-7 max-w-2xl text-pretty text-lg md:text-xl text-ink-soft leading-relaxed"
        >
          {detail.heroSub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-10 flex flex-wrap gap-3"
        >
          <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity">
            Let's Talk
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/projects" className="inline-flex items-center gap-2 rounded-full border border-border-strong px-7 py-3.5 text-sm font-medium text-foreground hover:bg-surface transition-colors">
            Explore Projects <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ───────────── Section wrapper ───────────── */
function Section({
  bg,
  eyebrow,
  title,
  intro,
  children,
}: {
  bg?: "surface";
  eyebrow: string;
  title: string;
  intro?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`py-28 ${bg === "surface" ? "bg-surface border-y border-border" : ""}`}>
      <div className="container-page">
        <Reveal>
          <div className="max-w-3xl mb-14">
            <span className="eyebrow text-primary">{eyebrow}</span>
            <h2 className="mt-4 text-balance text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              {title}
            </h2>
            {intro && <p className="mt-5 text-pretty text-base md:text-lg text-ink-soft leading-relaxed">{intro}</p>}
          </div>
        </Reveal>
        {children}
      </div>
    </section>
  );
}

/* ───────────── Final CTA ───────────── */
function FinalCTA() {
  return (
    <section className="py-32">
      <div className="container-page">
        <div className="relative overflow-hidden rounded-3xl bg-foreground text-background p-12 md:p-20">
          <div
            className="absolute inset-0 opacity-50"
            style={{
              background:
                "radial-gradient(60% 70% at 80% 20%, color-mix(in oklab, var(--primary) 60%, transparent), transparent 60%), radial-gradient(40% 60% at 0% 100%, color-mix(in oklab, var(--accent) 35%, transparent), transparent 60%)",
            }}
          />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="eyebrow" style={{ color: "color-mix(in oklab, white 70%, transparent)" }}>
                Next Step
              </span>
              <h2 className="mt-5 text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
                Let's Design Intelligent Environments
              </h2>
            </div>
            <div className="flex flex-col gap-6">
              <p className="text-lg text-background/70 leading-relaxed max-w-md">
                A private consultation to scope your environment, the threats it faces, and the architecture that will serve it for the next decade.
              </p>
              <p className="text-sm text-background/60 leading-relaxed max-w-md">
                Arif Khan supports project planning and advisory requirements across Delhi NCR — including Delhi, New Delhi, Gurugram, Gurgaon, Faridabad, Ghaziabad and Meerut — along with selected projects in Mumbai, Lucknow, Bengaluru and North India.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-background text-foreground px-7 py-3.5 text-sm font-medium hover:opacity-90 transition-opacity">
                  Let's Talk <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/projects" className="inline-flex items-center gap-2 rounded-full border border-white/20 text-background px-7 py-3.5 text-sm font-medium hover:bg-white/5 transition-colors">
                  Explore Projects <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
